import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Target, Zap, Clock, Settings2, ChevronDown, Check } from 'lucide-react';
import type { SimulateNewRuleRequest, AggressivenessOptions } from '@/types/price-rules';

interface Props {
    formData: Partial<SimulateNewRuleRequest>;
    setFormData: (data: Partial<SimulateNewRuleRequest>) => void;
    aggressivenessOptions: AggressivenessOptions;
}

export default function StepConfig({ formData, setFormData, aggressivenessOptions }: Props) {
    const { levels } = aggressivenessOptions;

    const findLevelByInterval = (interval: number) => levels.find(l => l.interval_minutes === interval)?.level || 0;

    const [useCustomInterval, setUseCustomInterval] = useState(() => !findLevelByInterval(formData.sync_interval_minutes || 1440));
    const [selectedLevel, setSelectedLevel] = useState(() => findLevelByInterval(formData.sync_interval_minutes || 1440) || 1);

    const [customHours, setCustomHours] = useState(() => Math.floor((formData.sync_interval_minutes || 60) / 60));
    const [customMinutes, setCustomMinutes] = useState(() => (formData.sync_interval_minutes || 60) % 60);

    const [pmrInputValue, setPmrInputValue] = useState(String(formData.pmr_target || 70));

    const handleLevelChange = (level: number) => {
        setSelectedLevel(level);
        const config = levels.find(l => l.level === level);
        if (config) setFormData({ ...formData, sync_interval_minutes: config.interval_minutes });
    };

    const handleCustomIntervalChange = (hours: number, minutes: number) => {
        setCustomHours(hours);
        setCustomMinutes(minutes);
        const totalMinutes = (hours * 60) + minutes;
        const finalMinutes = Math.max(totalMinutes, aggressivenessOptions.custom_min_minutes);
        setFormData({ ...formData, sync_interval_minutes: finalMinutes });
    };

    const handlePmrInputChange = (value: string) => {
        setPmrInputValue(value.replace(/[^0-9]/g, ''));
    };

    const handlePmrBlur = () => {
        const numValue = parseInt(pmrInputValue) || 70;
        const clampedValue = Math.min(100, Math.max(10, numValue));
        setPmrInputValue(String(clampedValue));
        setFormData({ ...formData, pmr_target: clampedValue });
    };

    const handlePmrQuickSelect = (value: number) => {
        setPmrInputValue(String(value));
        setFormData({ ...formData, pmr_target: value });
    };

    const formatInterval = (minutes: number) => {
        if (minutes >= 1440) {
            const days = minutes / 1440;
            return days === 1 ? '1 dia' : `${days} dias`;
        }
        if (minutes >= 60) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            if (mins === 0) return hours === 1 ? '1 hora' : `${hours} horas`;
            return `${hours}h ${mins}min`;
        }
        return `${minutes} minutos`;
    };

    const getLevelConfig = (level: number) => {
        const configs: Record<number, { color: string; bg: string; border: string; ring: string }> = {
            1: { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: 'ring-emerald-500' },
            2: { color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', ring: 'ring-blue-500' },
            3: { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', ring: 'ring-amber-500' },
            4: { color: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200', ring: 'ring-rose-500' },
        };
        return configs[level] || { color: 'text-gray-700', bg: 'bg-gray-50', border: 'border-gray-200', ring: 'ring-gray-500' };
    };

    const hourOptions = Array.from({ length: 169 }, (_, i) => i);
    const minuteOptions = [0, 5, 10, 15, 20, 30, 45];

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold text-gray-900">Configuração da Regra</h2>
                <p className="text-sm text-gray-500 mt-1">Defina a meta de PMR e frequência de sincronização.</p>
            </div>

            {/* PMR Target + Base do Cálculo lado a lado */}
            <div className="grid gap-6 md:grid-cols-2">
                {/* PMR Target */}
                <div className="space-y-3">
                    <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <div className="p-1.5 bg-blue-100 rounded-md">
                            <Target className="h-4 w-4 text-blue-600" />
                        </div>
                        Meta de PMR
                    </Label>

                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Input
                                type="text"
                                inputMode="numeric"
                                value={pmrInputValue}
                                onChange={(e) => handlePmrInputChange(e.target.value)}
                                onBlur={handlePmrBlur}
                                className="w-20 h-11 text-center text-lg font-bold pr-7 bg-white border-2 focus:border-blue-500"
                            />
                            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">%</span>
                        </div>

                        <div className="flex gap-1.5">
                            {[50, 60, 70, 80, 90].map((val) => (
                                <button
                                    key={val}
                                    type="button"
                                    onClick={() => handlePmrQuickSelect(val)}
                                    className={`w-10 h-11 text-xs font-semibold rounded-lg border-2 transition-all ${
                                        formData.pmr_target === val
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                                    }`}
                                >
                                    {val}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Base do Cálculo */}
                <div className="space-y-3">
                    <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <div className="p-1.5 bg-orange-100 rounded-md">
                            <Settings2 className="h-4 w-4 text-orange-600" />
                        </div>
                        Base do Cálculo
                    </Label>

                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setFormData({ ...formData, ecpm_reference: 'rule_price' })}
                            className={`flex-1 h-11 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                                formData.ecpm_reference === 'rule_price'
                                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                            }`}
                        >
                            {formData.ecpm_reference === 'rule_price' && <Check className="h-4 w-4" />}
                            Price da Regra
                        </button>

                        <button
                            type="button"
                            onClick={() => setFormData({ ...formData, ecpm_reference: 'gam_ecpm' })}
                            className={`flex-1 h-11 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                                formData.ecpm_reference === 'gam_ecpm'
                                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                            }`}
                        >
                            {formData.ecpm_reference === 'gam_ecpm' && <Check className="h-4 w-4" />}
                            eCPM do GAM
                        </button>
                    </div>
                </div>
            </div>

            {/* Dica */}
            <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                💡 PMR abaixo da meta → preço <span className="text-red-600 font-medium">desce</span>.
                PMR acima da meta → preço <span className="text-green-600 font-medium">sobe</span>.
                <br />
                ⏱ O sync automático só muda o price depois de <b>um dia inteiro de dados do GAM com o price atual</b>
                — a primeira mudança vem no 2º dia após criar, e cada ajuste espera outro dia completo. A agressividade
                define com que frequência ele confere, não quantas vezes muda por dia.
            </p>

            {/* Sync Interval */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <div className="p-1.5 bg-purple-100 rounded-md">
                            <Zap className="h-4 w-4 text-purple-600" />
                        </div>
                        Agressividade do Sync
                    </Label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <span className={`text-xs ${useCustomInterval ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                            Personalizado
                        </span>
                        <Switch
                            checked={useCustomInterval}
                            onCheckedChange={(v) => {
                                setUseCustomInterval(v);
                                if (!v) handleLevelChange(selectedLevel);
                            }}
                        />
                    </label>
                </div>

                {!useCustomInterval ? (
                    <div className="grid grid-cols-4 gap-2">
                        {levels.map((level) => {
                            const config = getLevelConfig(level.level);
                            const isSelected = selectedLevel === level.level;
                            return (
                                <button
                                    key={level.level}
                                    type="button"
                                    onClick={() => handleLevelChange(level.level)}
                                    className={`relative p-3 rounded-xl border-2 text-center transition-all ${config.bg} ${config.border} ${
                                        isSelected ? `ring-2 ring-offset-1 ${config.ring}` : 'hover:shadow-md'
                                    }`}
                                >
                                    <div className={`text-2xl font-bold ${config.color}`}>{level.level}</div>
                                    <div className={`text-xs font-semibold ${config.color}`}>{level.label}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">{level.description}</div>
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl bg-white">
                        <Clock className="h-5 w-5 text-gray-400" />

                        <div className="flex items-center gap-2">
                            <div className="relative">
                                <select
                                    value={customHours}
                                    onChange={(e) => handleCustomIntervalChange(parseInt(e.target.value), customMinutes)}
                                    className="h-10 pl-3 pr-8 text-sm font-medium border-2 border-gray-200 rounded-lg bg-white appearance-none cursor-pointer focus:outline-none focus:border-purple-500"
                                >
                                    {hourOptions.map((h) => (
                                        <option key={h} value={h}>{h}h</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                            </div>

                            <span className="text-gray-400">:</span>

                            <div className="relative">
                                <select
                                    value={customMinutes}
                                    onChange={(e) => handleCustomIntervalChange(customHours, parseInt(e.target.value))}
                                    className="h-10 pl-3 pr-8 text-sm font-medium border-2 border-gray-200 rounded-lg bg-white appearance-none cursor-pointer focus:outline-none focus:border-purple-500"
                                >
                                    {minuteOptions.map((m) => (
                                        <option key={m} value={m}>{m}min</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                            </div>
                        </div>

                        <div className="flex-1 text-right">
                            <span className="text-sm font-bold text-purple-600">{formatInterval(formData.sync_interval_minutes || 60)}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Summary */}
            <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Resumo</h4>
                <div className="flex flex-wrap gap-3">
                    <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
                        <span className="text-xs text-gray-500">Meta PMR</span>
                        <span className="text-base font-bold text-gray-900 ml-2">{formData.pmr_target}%</span>
                    </div>
                    <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
                        <span className="text-xs text-gray-500">Intervalo</span>
                        <span className="text-base font-bold text-gray-900 ml-2">{formatInterval(formData.sync_interval_minutes || 1440)}</span>
                    </div>
                    <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
                        <span className="text-xs text-gray-500">Base</span>
                        <span className="text-base font-bold text-gray-900 ml-2">{formData.ecpm_reference === 'rule_price' ? 'Price' : 'eCPM'}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}